import type { FC, ReactNode } from 'react'

import {
    Mail,
    MapPin,
    Phone,
    Clock
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/generals/reveal'
import { Section } from '@/components/generals/section'
import { PageHero } from '@/components/sections/page-hero'
import { ContactForm } from '@/components/sections/contact-form'
import { Faq } from '@/components/sections/faq'

const details = [
    {
        icon: Mail,
        label: 'Email us',
        value: 'hello@funnel.agency',
        href: 'mailto:hello@funnel.agency'
    },
    {
        icon: Phone,
        label: 'Call us',
        value: '+44 20 7946 0318',
        href: 'tel:+442079460318'
    },
    {
        icon: MapPin,
        label: 'Studio',
        value: '18 Hanbury Street, London E1 6QR',
        href: null
    },
    {
        icon: Clock,
        label: 'Response time',
        value: 'Within one business day',
        href: null
    }
]

const steps = [
    {
        title: 'You tell us where you are',
        body: 'Share your current spend, your results and the goal you are chasing. The more context, the sharper our first call.'
    },
    {
        title: 'We audit before we pitch',
        body: 'We review your funnel, creative and account structure, then come back with what we would change and why.'
    },
    {
        title: 'You get a plan and a number',
        body: 'A 15-minute call, a clear 90-day plan, and the honest answer on whether paid ads are the right lever for you.'
    }
]

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description='Book a 15-minute call with Funnel. Tell us about your brand and we will come back with an honest audit and a clear plan for paid growth.'
            />

            <PageHero
                showGlow
                showLogos={false}
                segments={[
                    { text: 'Let’s talk about' },
                    { text: 'your growth.', serif: true }
                ]}
                subtitle='Tell us where your brand is today and where you want it to be. We’ll come back with an honest read on your funnel and a plan to scale it profitably.'
            />

            <Section
                id='contact'
                className='pt-0 pb-[100px] lg:pt-0 lg:pb-[120px]'
                containerClassName='items-stretch'
            >
                <div className='grid w-full grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-12'>
                    <div className='flex flex-col gap-8'>
                        <Reveal
                            y={24}
                            blur={10}
                            stagger={0.08}
                            className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1'
                        >
                            {details.map((detail) => {
                                const Icon = detail.icon

                                const inner = (
                                    <>
                                        <span
                                            aria-hidden='true'
                                            className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/6 text-[#8b5cf6] ring-1 ring-white/8'
                                        >
                                            <Icon className='size-4.5' />
                                        </span>

                                        <span className='flex flex-col gap-1'>
                                            <span className='text-sm leading-none tracking-[-0.01em] text-white/40'>
                                                {detail.label}
                                            </span>

                                            <span className='text-[15px] leading-tight font-medium tracking-[-0.01em] text-white'>
                                                {detail.value}
                                            </span>
                                        </span>
                                    </>
                                )

                                if (detail.href) {
                                    return (
                                        <a
                                            key={detail.label}
                                            href={detail.href}
                                            className='flex items-center gap-4 rounded-2xl bg-surface-1 p-4 ring-1 ring-white/8 transition-all duration-500 ease-[cubic-bezier(0.44,0,0.56,1)] hover:-translate-y-0.5 hover:ring-white/16'
                                        >
                                            {inner}
                                        </a>
                                    )
                                }

                                return (
                                    <div
                                        key={detail.label}
                                        className='flex items-center gap-4 rounded-2xl bg-surface-1 p-4 ring-1 ring-white/8'
                                    >
                                        {inner}
                                    </div>
                                )
                            })}
                        </Reveal>

                        <Reveal
                            y={24}
                            blur={10}
                            delay={0.1}
                            className='flex flex-col gap-6 rounded-2xl bg-surface-1 p-6 ring-1 ring-white/8 sm:p-8'
                        >
                            <h2 className='text-xl leading-none font-medium tracking-[-0.03em] text-white'>
                                What happens next
                            </h2>

                            <ol className='flex flex-col gap-6'>
                                {steps.map((step, index) => (
                                    <li
                                        key={step.title}
                                        className='flex gap-4'
                                    >
                                        <span
                                            aria-hidden='true'
                                            className='flex size-7 shrink-0 items-center justify-center rounded-full bg-[#8b5cf6] text-[13px] font-medium text-white'
                                        >
                                            {index + 1}
                                        </span>

                                        <span className='flex flex-col gap-1.5'>
                                            <span className='text-[15px] leading-none font-medium tracking-[-0.01em] text-white'>
                                                {step.title}
                                            </span>

                                            <span className='text-[15px] leading-[1.6] tracking-[-0.01em] text-white/56'>
                                                {step.body}
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </Reveal>
                    </div>

                    <Reveal
                        y={30}
                        blur={12}
                        delay={0.06}
                    >
                        <ContactForm />
                    </Reveal>
                </div>
            </Section>

            <Faq
                eyebrow='FAQ'
                segments={[
                    { text: 'Questions people ask before the' },
                    { text: 'first call.', serif: true }
                ]}
            />
        </Layout>
    )
}

export default Contact
